import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faPen, faTrash, faBell } from '@fortawesome/free-solid-svg-icons';
import { ecourtApi } from './ecourtApi';
import { ecPath, fmtDate, normalizeCnr, CNR_RE, statusBadge, updateText, money, sleep } from './ecourtUtils';
import { Loader, ErrorBox, Empty, PageHeader, Cost, useBalance } from './EcourtUi';

const ROLES = [['', 'Role (optional)'], ['petitioner', 'Petitioner side'], ['respondent', 'Respondent side'], ['other', 'Other']];

function AddCaseForm({ onAdded }) {
  const { note } = useBalance();
  const [cnr, setCnr] = useState('');
  const [label, setLabel] = useState('');
  const [clientName, setClientName] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const c = normalizeCnr(cnr);
    if (!CNR_RE.test(c)) { setErr('Enter a 16-character CNR (4 letters + 12 digits).'); return; }
    setBusy(true);
    setError(null);
    try {
      note(await ecourtApi.addMyCase({ cnr: c, label: label.trim() || undefined, clientName: clientName.trim() || undefined, role: role || undefined }));
      setCnr(''); setLabel(''); setClientName(''); setRole('');
      onAdded();
    } catch (ex) {
      setError(ex);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ec-card mb-3" onSubmit={submit} noValidate>
      <div className="row g-2 align-items-end">
        <div className="col-12 col-md-3">
          <label htmlFor="mc-cnr" className="ec-label">CNR</label>
          <input id="mc-cnr" className="form-control ec-mono" value={cnr} maxLength={20} placeholder="TNCB010012342025"
            onChange={(e) => { setCnr(e.target.value); setErr(''); }} aria-invalid={Boolean(err)} />
        </div>
        <div className="col-12 col-md-3">
          <label htmlFor="mc-label" className="ec-label">Your name for it (optional)</label>
          <input id="mc-label" className="form-control" value={label} maxLength={160} placeholder="e.g. Kumar land dispute" onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="col-12 col-md-2">
          <label htmlFor="mc-client" className="ec-label">Client (optional)</label>
          <input id="mc-client" className="form-control" value={clientName} maxLength={160} onChange={(e) => setClientName(e.target.value)} />
        </div>
        <div className="col-12 col-md-2">
          <label htmlFor="mc-role" className="ec-label">Side</label>
          <select id="mc-role" className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="col-12 col-md-2 d-grid">
          <button type="submit" className="btn btn-ec" disabled={busy}>{busy ? 'Adding…' : 'Add case'}</button>
        </div>
      </div>
      {err && <div className="ec-field-error">{err}</div>}
      <div className="small ec-muted mt-2">Free when the case is already saved; otherwise it is fetched once (<Cost kind="case_detail" />).</div>
      <ErrorBox error={error} />
    </form>
  );
}

function ConfirmModal({ show, title, message, onOk, onCancel, busy }) {
  if (!show) return null;
  return (
    <div className="ec-modal-backdrop" onClick={onCancel}>
      <div className="ec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ec-modal-title">{title}</div>
        <div className="ec-modal-body">{message}</div>
        <div className="ec-modal-footer">
          <button type="button" className="btn btn-sm btn-ec-outline" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-sm btn-danger" onClick={onOk} disabled={busy}>{busy ? 'Please wait…' : 'OK'}</button>
        </div>
      </div>
    </div>
  );
}

function CaseCard({ c, onChanged, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ label: c.label || '', clientName: c.client_name || '', notes: c.notes || '' });
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const showUpdates = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    try {
      const r = await ecourtApi.myCaseUpdates(c.cnr);
      setUpdates(r.updates || []);
      if (c.new_updates) { await ecourtApi.markSeen(c.id); onChanged(); }
    } catch (e) { setError(e); }
  };

  const save = async () => {
    try {
      await ecourtApi.updateMyCase(c.id, form);
      setEditing(false);
      onChanged();
    } catch (e) { setError(e); }
  };

  const toggleAuto = async () => {
    try { await ecourtApi.updateMyCase(c.id, { autoSync: !c.auto_sync }); onChanged(); } catch (e) { setError(e); }
  };

  const remove = async () => {
    setRemoving(true);
    try { await ecourtApi.removeMyCase(c.id); setConfirmRemove(false); onRemoved(); } catch (e) { setError(e); setConfirmRemove(false); } finally { setRemoving(false); }
  };

  const pending = String(c.case_status || '').toUpperCase() !== 'DISPOSED';

  return (
    <article className="ec-card ec-card-tight mb-2">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div className="ec-minw0">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Link to={ecPath(`case/${c.cnr}`)} className="ec-result-title ec-link-plain">{c.label || c.title || c.cnr}</Link>
            {c.case_status && <span className={statusBadge(c.case_status)}>{c.case_status}</span>}
            {c.new_updates > 0 && <span className="ec-badge ec-badge-yellow"><FontAwesomeIcon icon={faBell} /> {c.new_updates} new</span>}
          </div>
          {c.label && c.title && <div className="small">{c.title}</div>}
          <div className="small ec-muted">
            <span className="ec-mono">{c.cnr}</span>{c.court_name ? ` · ${c.court_name}` : ''}{c.case_type_label ? ` · ${c.case_type_label}` : ''}
            {c.client_name ? ` · Client: ${c.client_name}` : ''}{c.role ? ` · ${c.role}` : ''}
          </div>
          {c.last_update && <div className="small mt-1">Latest: {updateText(c.last_update)} <span className="ec-muted">({c.last_update.at})</span></div>}
          {c.notes && !editing && <div className="small ec-muted mt-1 ec-prewrap">{c.notes}</div>}
        </div>
        <div className="text-md-end">
          <div className="ec-overline">{pending ? 'Next hearing' : 'Status'}</div>
          <div className="fw-bold">{pending ? fmtDate(c.next_hearing_date) : 'Disposed'}</div>
          <div className="small ec-muted">{c.last_synced_at ? `Synced ${c.last_synced_at}` : c.fetched_at ? `Saved ${c.fetched_at}` : ''}</div>
        </div>
      </div>

      {editing && (
        <div className="row g-2 mt-2">
          <div className="col-12 col-md-4"><input className="form-control" aria-label="Your name for the case" placeholder="Your name for it" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
          <div className="col-12 col-md-3"><input className="form-control" aria-label="Client" placeholder="Client" value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} /></div>
          <div className="col-12 col-md-5"><input className="form-control" aria-label="Notes" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="col-12 d-flex gap-2">
            <button type="button" className="btn btn-sm btn-ec" onClick={save}>Save</button>
            <button type="button" className="btn btn-sm btn-ec-outline" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
        <Link to={ecPath(`case/${c.cnr}`)} className="btn btn-sm btn-ec-outline">Open</Link>
        <button type="button" className="btn btn-sm btn-ec-outline" onClick={showUpdates} aria-expanded={open}>{open ? 'Hide updates' : 'Updates'}</button>
        {!editing && <button type="button" className="btn btn-sm btn-ec-outline" onClick={() => setEditing(true)}><FontAwesomeIcon icon={faPen} /> Edit</button>}
        <div className="form-check form-switch m-0 ms-1">
          <input className="form-check-input" type="checkbox" role="switch" id={`mc-auto-${c.id}`} checked={c.auto_sync} onChange={toggleAuto} />
          <label className="form-check-label small" htmlFor={`mc-auto-${c.id}`}>Nightly sync</label>
        </div>
        <button type="button" className="btn btn-sm btn-link ec-link ms-auto" onClick={() => setConfirmRemove(true)}><FontAwesomeIcon icon={faTrash} /> Remove</button>
      </div>

      <ConfirmModal
        show={confirmRemove}
        title="Remove case"
        message={`Remove "${c.label || c.cnr}" from My Cases? The case itself is not affected.`}
        onOk={remove}
        onCancel={() => setConfirmRemove(false)}
        busy={removing}
      />

      {open && (
        <div className="ec-updates mt-2">
          {updates === null ? <Loader label="Loading updates…" /> : updates.length === 0
            ? <div className="small ec-muted">No changes recorded yet. Use Sync to check eCourts now.</div>
            : updates.map((u) => (
              <div key={u.id} className="ec-alert-row small d-flex gap-2">
                <span className="ec-muted ec-date-col">{u.created_at}</span>
                <span className="flex-grow-1">{updateText(u)}</span>
                {u.event_type === 'new_order' && u.details && u.details.orderFile && (
                  <Link className="ec-link" to={ecPath(`case/${c.cnr}/order/${encodeURIComponent(u.details.orderFile)}`)}>Open order</Link>
                )}
              </div>
            ))}
        </div>
      )}
      <ErrorBox error={error} />
    </article>
  );
}

export default function MyCases() {
  const { note } = useBalance();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const r = note(await ecourtApi.myCases());
      if (alive.current) setData(r);
    } catch (e) {
      if (alive.current) setError(e);
    }
  }, [note]);

  useEffect(() => { load(); }, [load]);

  const [confirmSync, setConfirmSync] = useState(false);

  const doSync = async () => {
    setConfirmSync(false);
    setSyncing(true);
    setError(null);
    setSyncNote('');
    try {
      await ecourtApi.syncMyCases();
      setSyncNote('Checking eCourts… this takes about a minute. The list updates by itself.');
      const before = JSON.stringify((data.cases || []).map((c) => c.last_synced_at));
      for (let i = 0; i < 24 && alive.current; i++) {
        await sleep(5000);
        const r = note(await ecourtApi.myCases());
        if (!alive.current) return;
        setData(r);
        if (JSON.stringify(r.cases.map((c) => c.last_synced_at)) !== before && r.cases.every((c) => c.last_synced_at)) break;
      }
      if (alive.current) setSyncNote('Sync finished.');
    } catch (e) {
      if (alive.current) setError(e);
    } finally {
      if (alive.current) setSyncing(false);
    }
  };

  const cases = (data && data.cases) || [];
  const newCount = cases.reduce((n, c) => n + (c.new_updates || 0), 0);

  return (
    <div>
      <PageHeader title="My Cases" subtitle="Your saved cases — next hearing, status and what changed. Opening a saved case is free.">
        {cases.length > 0 && (
          <button type="button" className="btn btn-ec-outline" onClick={() => cases.length ? setConfirmSync(true) : null} disabled={syncing}>
            <FontAwesomeIcon icon={faArrowsRotate} spin={syncing} /> {syncing ? 'Syncing…' : 'Sync all now'}
            {!syncing && data && <span className="ec-cost">{Number(data.syncPricePerCase).toFixed(2)} × {cases.length}</span>}
          </button>
        )}
      </PageHeader>

      <ConfirmModal
        show={confirmSync}
        title="Sync all cases"
        message={`Check all ${cases.length} cases on eCourts now? This costs ${money(((data && data.syncPricePerCase) || 0) * cases.length)} (${money((data && data.syncPricePerCase) || 0)} per case).`}
        onOk={doSync}
        onCancel={() => setConfirmSync(false)}
        busy={false}
      />

      <AddCaseForm onAdded={load} />
      <ErrorBox error={error} />
      {syncNote && <div className="ec-alert ec-alert-success mb-3" role="status">{syncNote}</div>}

      {data && (
        <div className="small ec-muted mb-2">
          {cases.length} case{cases.length === 1 ? '' : 's'}{newCount ? ` · ${newCount} new update${newCount > 1 ? 's' : ''}` : ''}
          {' · '}Nightly sync is {data.autoSyncOn ? `on (${money(data.syncPricePerCase)} per case each night, for cases with Nightly sync switched on)` : 'off on this server — use Sync all now'}.
          {newCount > 0 && <button type="button" className="btn btn-link ec-link btn-sm p-0 ms-2" onClick={async () => { await ecourtApi.markSeen(); load(); }}>Mark all seen</button>}
        </div>
      )}

      {data === null && !error && <Loader label="Loading your cases…" />}
      {data && cases.length === 0 && <Empty>No saved cases yet. Add one above by CNR, or open a case and press “Add to My Cases”.</Empty>}
      {cases.map((c) => <CaseCard key={c.id} c={c} onChanged={load} onRemoved={load} />)}
    </div>
  );
}